import React, { useState, useEffect, useMemo } from "react";
import {
    TextField,
    Button,
    Box,
    Typography,
    FormControlLabel,
    Checkbox,
    Tooltip,
    CircularProgress,
    Paper,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Slide
} from "@mui/material";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import CloseIcon from "@mui/icons-material/Close";
import { PrivacyContent } from "./PrivacyContent.js";

import { collection, addDoc, doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";

import { PiprChart } from "./components/pipr_chart";

const darkTheme = createTheme({
    palette: {
        mode: "dark",
        background: {
            default: "#202124",
            paper: "#202124",
        },
        text: {
            primary: "#f5f5f5",
        },
        primary: {
            main: "#f5f5f5",
        },
    },
    typography: {
        fontFamily: "sans-serif",
    },
});

const translations = {
    de: {
        title: "Zinsprognose",
        chartLead: "Erwarteter Leitzins je Beschluss mit Wahrscheinlichkeiten.",
        building: "Bauen.",
        emailLabel: "E-Mail",
        investment: "Investment",
        analytics: "Analytics",
        submitButton: "Eintragen",
        formDescription: "Neuigkeiten zu unseren Services per E-Mail.",
        successMessage: "Danke! Bitte bestätigen Sie Ihre E-Mail-Adresse über den Link, den wir Ihnen gesendet haben.",
        errorMessage: "Etwas ist schief gelaufen. Bitte versuchen Sie es erneut.",
        analyticsTooltip:
            "Informationen zu unserer Technologie und Plattform. Wenn aktiviert, senden wir Informationen, sobald unsere Plattform startet und wir unsere Technologie lizenzieren oder anbieten.",
        investmentTooltip:
            "Informationen werden verschickt, sobald die Möglichkeit besteht zu investieren.",
        verificationSuccess: "Erfolgreich registriert!",
        verificationSuccessDetails: "Sie erhalten ab sofort Updates zu unseren Services:",
        verificationError: "Bestätigung fehlgeschlagen",
        verificationErrorDetails: "Der Bestätigungslink ist ungültig oder abgelaufen.",
        loading: "Wird geladen...",
        unsubscribeSuccess: "Erfolgreich abgemeldet!",
        unsubscribeSuccessDetails: "Sie wurden erfolgreich von unserem Newsletter abgemeldet:",
        unsubscribeError: "Abmeldung fehlgeschlagen",
        unsubscribeErrorDetails: "Der Abmeldelink ist ungültig oder abgelaufen.",
        imprintTitle: "Impressum",
        imprintContent: `Newgate AG
            Alte Churerstrasse 47
            9496 Balzers
            Liechtenstein

            Email: `,
        privacyTitle: "Datenschutzerklärung",
        privacyContent: "Es werden ausschließlich die für den Betrieb der Dienste erforderlichen Daten gespeichert. Personenbezogene Daten, wie E-Mail-Adresse und Präferenzen, dienen ausschließlich der Bereitstellung und Verbesserung der Services. Eine Weitergabe an Dritte erfolgt nicht.\n\nNewsletter\nZum Versand des Newsletters werden ausschließlich die hierfür erforderlichen personenbezogenen Daten verarbeitet. Die Verarbeitung erfolgt auf Grundlage der Einwilligung gemäß Art. 6 Abs. 1 lit. a DSGVO. Es erfolgt keine Weitergabe der Daten an Dritte. Die Speicherung erfolgt nur, solange das Newsletter-Abonnement aktiv ist. Eine Abmeldung ist jederzeit über den im Newsletter enthaltenen Link möglich. Nach Abmeldung werden die Daten gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.",
        privacyLink: "Datenschutz",
        close: "Schließen",
        nudgeText: "Schon für den Newsletter angemeldet?",
        nudgeGoto: "Zur Anmeldung springen",
        nudgeClose: "Hinweis schließen"
    },
    en: {
        title: "Rate Forecast",
        chartLead: "Expected policy rate per decision with probabilities.",
        building: "Building.",
        emailLabel: "Email",
        investment: "Investment",
        analytics: "Analytics",
        submitButton: "Notify me",
        formDescription: "Updates on our services by email.",
        successMessage: "Thank you! Please confirm your email address using the link we sent you.",
        errorMessage: "Something went wrong. Please try again.",
        analyticsTooltip:
            "Information about our analytics services and platform. If checked, we will send information when our platform launches and about licensing or offering our technology.",
        investmentTooltip:
            "Information will be sent once there is the opportunity to invest.",
        verificationSuccess: "Successfully registered!",
        verificationSuccessDetails: "You will now receive updates about our services:",
        verificationError: "Verification failed",
        verificationErrorDetails: "The verification link is invalid or expired.",
        loading: "Loading...",
        unsubscribeSuccess: "Successfully unsubscribed!",
        unsubscribeSuccessDetails: "You have been successfully unsubscribed from our newsletter:",
        unsubscribeError: "Unsubscribe failed",
        unsubscribeErrorDetails: "The unsubscribe link is invalid or expired.",
        imprintTitle: "Imprint",
        imprintContent: `Newgate AG
            Alte Churerstrasse 47
            9496 Balzers
            Liechtenstein

            Email: `,
        privacyTitle: "Privacy Policy",
        privacyContent: "Only data necessary for the operation of the services is stored. Personal data, such as email address and preferences, is used solely for the provision and improvement of the services. No data is shared with third parties.\n\nNewsletter\nFor the purpose of sending newsletters, only the personal data required for this function is processed. Processing is based on consent in accordance with Art. 6(1)(a) GDPR. No data is shared with third parties. Data is stored only for as long as the newsletter subscription remains active. Unsubscription is possible at any time via the link included in each newsletter. After unsubscribing, the data will be deleted unless legal retention obligations apply.",
        privacyLink: "Privacy",
        close: "Close",
        nudgeText: "Already signed up for the newsletter?",
        nudgeGoto: "Jump to the sign-up form",
        nudgeClose: "Dismiss"
    },
};

function App() {
    const [language, setLanguage] = useState("de");
    const [formData, setFormData] = useState({
        email: "",
        investment: false,
        analytics: false,
        language: "de",
    });

    const localPart = 'contact';       // left side vor @
    const domain = 'newgate.li';      // domain
    const email = `${localPart}@${domain}`;

    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("success");

    // Verification states
    const [verificationMode, setVerificationMode] = useState(false);
    const [verificationLoading, setVerificationLoading] = useState(false);
    const [verificationData, setVerificationData] = useState(null);
    const [verificationError, setVerificationError] = useState(false);

    // Unsubscribe states
    const [unsubscribeMode, setUnsubscribeMode] = useState(false);
    const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
    const [unsubscribeData, setUnsubscribeData] = useState(null);
    const [unsubscribeError, setUnsubscribeError] = useState(false);

    const [nudgeOpen, setNudgeOpen] = useState(false);
    const [nudgeDone, setNudgeDone] = useState(false);
    const [atPageBottom, setAtPageBottom] = useState(false);

    const [honeypotName, setHoneypotName] = useState("");
    const [honeypotValue, setHoneypotValue] = useState("");
    // generate a realistic base name list to pick from
    const realisticNames = useMemo(() => [
        "phone",
        "company",
        "website",
        "address2",
        "middleName",
        "referrer",
        "contact",
        "jobTitle",
    ], []);

    const [imprintOpen, setImprintOpen] = useState(false);
    const [privacyOpen, setPrivacyOpen] = useState(false);

    const t = translations[language];

    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const verifyId = urlParams.get('verify');
        const unsubscribeId = urlParams.get('unsubscribe');

        if (verifyId) {
            setVerificationMode(true);
            setVerificationLoading(true);

            const fetchSubscriber = async () => {
                try {
                    const docRef = doc(db, "subscribers", verifyId);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setVerificationData(data);
                        const userLang = data.language || "de";
                        setLanguage(userLang);

                        await updateDoc(docRef, {
                            approved: true,
                            verified: true
                        });

                        setSeverity("success");
                        setMessage(translations[userLang].verificationSuccess);
                        setOpen(true);
                    } else {
                        const currentLang = language;
                        setVerificationError(true);
                        setSeverity("error");
                        setMessage(translations[currentLang].verificationError);
                        setOpen(true);
                    }
                } catch (error) {
                    const currentLang = language;
                    setVerificationError(true);
                    setSeverity("error");
                    setMessage(translations[currentLang].verificationError);
                    setOpen(true);
                } finally {
                    setVerificationLoading(false);
                }
            };

            fetchSubscriber();
        } else if (unsubscribeId) {
            setUnsubscribeMode(true);
            setUnsubscribeLoading(true);

            const unsubscribeUser = async () => {
                try {
                    const docRef = doc(db, "subscribers", unsubscribeId);
                    const docSnap = await getDoc(docRef);

                    if (docSnap.exists()) {
                        const data = docSnap.data();
                        setUnsubscribeData(data);
                        const userLang = data.language || "de";
                        setLanguage(userLang);

                        await updateDoc(docRef, {
                            subscribed: false
                        });

                        setSeverity("success");
                        setMessage(translations[userLang].unsubscribeSuccess);
                        setOpen(true);
                    } else {
                        const currentLang = language;
                        setUnsubscribeError(true);
                        setSeverity("error");
                        setMessage(translations[currentLang].unsubscribeError);
                        setOpen(true);
                    }
                } catch (error) {
                    const currentLang = language;
                    setUnsubscribeError(true);
                    setSeverity("error");
                    setMessage(translations[currentLang].unsubscribeError);
                    setOpen(true);
                } finally {
                    setUnsubscribeLoading(false);
                }
            };

            unsubscribeUser();
        }
    }, [language]);

    useEffect(() => {
        if (verificationMode || unsubscribeMode || nudgeDone) return;
        const timer = setTimeout(() => setNudgeOpen(true), 30000);
        return () => clearTimeout(timer);
    }, [verificationMode, unsubscribeMode, nudgeDone]);

    useEffect(() => {
        const target = document.getElementById("signup");
        if (!target) return;
        const observer = new IntersectionObserver(
            (entries) => {
                if (window.scrollY <= 0) return;
                if (entries.some((entry) => entry.isIntersecting)) {
                    setNudgeOpen(false);
                    setNudgeDone(true);
                    observer.disconnect();
                }
            },
            { threshold: 0.4 },
        );
        observer.observe(target);
        return () => observer.disconnect();
    }, [verificationMode, unsubscribeMode]);

    useEffect(() => {
        const update = () => {
            const doc = document.documentElement;
            const remaining = doc.scrollHeight - window.innerHeight - window.scrollY;
            setAtPageBottom(remaining <= 24);
        };
        update();
        window.addEventListener("scroll", update, { passive: true });
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update);
            window.removeEventListener("resize", update);
        };
    }, []);

    const dismissNudge = () => {
        setNudgeOpen(false);
        setNudgeDone(true);
    };

    const scrollToSignup = () => {
        const target = document.getElementById("signup");
        if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
        dismissNudge();
    };

    const toggleLanguage = () => {
        const newLanguage = language === "de" ? "en" : "de";
        setLanguage(newLanguage);
        setFormData((prev) => ({ ...prev, language: newLanguage }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.email) return;

        // --- Honeypot check ---
        if (honeypotValue && honeypotValue.trim() !== "") {
            console.warn("Bot detected via honeypot:", honeypotName);
            return;
        }

        try {
            await addDoc(collection(db, "subscribers"), {
                ...formData,
                timestamp: new Date(),
                processed: false,
            });

            setSeverity("success");
            setMessage(t.successMessage);
            setOpen(true);

            setNudgeOpen(false);
            setNudgeDone(true);

            setFormData((prev) => ({
                ...prev,
                email: "",
                investment: false,
                analytics: false,
            }));
        } catch (error) {
            setSeverity("error");
            setMessage(t.errorMessage);
            setOpen(true);
        }
    };

    const handleCheckboxChange = (field) => (e) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.checked }));
    };

    const handleClose = () => {
        setOpen(false);
    };

    const isValidEmail = (email) => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
    };

    const renderVerificationContent = () => {
        if (verificationLoading) {
            return (
                <Box sx={{ textAlign: "center", py: 3 }}>
                    <CircularProgress sx={{ color: "#f5f5f5" }} />
                </Box>
            );
        }

        return (
            <Paper sx={{
                textAlign: "center",
                p: 2,
                backgroundColor: "#1b1b1c",
                borderRadius: "8px",
                width: "100%",
            }}>
                <Typography variant="body1" sx={{ color: "#f5f5f5", opacity: 0.8 }}>
                    {verificationError ? t.verificationErrorDetails : t.verificationSuccessDetails}
                </Typography>
                {!verificationError && verificationData?.email && (
                    <Typography variant="body1" sx={{ color: "#f5f5f5", fontWeight: "500" }}>
                        {verificationData.email}
                    </Typography>
                )}
            </Paper>
        );
    };

    const renderUnsubscribeContent = () => {
        if (unsubscribeLoading) {
            return (
                <Box sx={{ textAlign: "center", py: 3 }}>
                    <CircularProgress sx={{ color: "#f5f5f5" }} />
                </Box>
            );
        }

        return (
            <Paper sx={{
                textAlign: "center",
                p: 2,
                backgroundColor: "#1b1b1c",
                borderRadius: "8px",
                width: "100%",
            }}>
                <Typography variant="body1" sx={{ color: "#f5f5f5", opacity: 0.8 }}>
                    {unsubscribeError ? t.unsubscribeErrorDetails : t.unsubscribeSuccessDetails}
                </Typography>
                {!unsubscribeError && unsubscribeData?.email && (
                    <Typography variant="body1" sx={{ color: "#f5f5f5", fontWeight: "500" }}>
                        {unsubscribeData.email}
                    </Typography>
                )}
            </Paper>
        );
    };

    useEffect(() => {
        const base = realisticNames[Math.floor(Math.random() * realisticNames.length)];
        const suffix = Math.random().toString(36).slice(2, 6);
        setHoneypotName(`${base}_${suffix}`);
    }, [realisticNames]);

    return (
        <ThemeProvider theme={darkTheme}>
            <CssBaseline />
            <Box
                sx={{
                    minHeight: "100vh",
                    display: "flex",
                    flexDirection: "column",
                    px: { xs: 2, md: 4, lg: 6 },
                    py: 2,
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: { xs: "1.5rem", md: "1.5rem" },
                    }}
                >
                    <a href="/">
                        <img
                            src="/logo.png"
                            alt="Newgate"
                            width="200"
                            style={{ cursor: "pointer" }}
                        />
                    </a>
                    <Button
                        onClick={toggleLanguage}
                        variant="outlined"
                        sx={{
                            minWidth: "32px",
                            height: "32px",
                            fontSize: "0.75rem",
                            padding: "4px",
                            color: "#f5f5f5",
                            borderColor: "#f5f5f5",
                            "&:hover": {
                                backgroundColor: "#f5f5f5",
                                color: "#202124",
                            },
                        }}
                    >
                        {language.toUpperCase()}
                    </Button>
                </Box>

                <Box component="main" sx={{ flexGrow: 1, width: "100%" }}>
                    <Box sx={{ width: "100%", maxWidth: 1440, mx: "auto" }}>
                        <Typography
                            variant="subtitle1"
                            sx={{ color: "#f5f5f5", opacity: 0.85, fontWeight: 500 }}
                        >
                            {t.title}
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#f5f5f5",
                                opacity: 0.55,
                                mb: 2,
                            }}
                        >
                            {t.chartLead}
                        </Typography>

                        <PiprChart language={language} />
                    </Box>

                    <Box
                        aria-hidden="true"
                        sx={{
                            width: "100%",
                            maxWidth: 1440,
                            mx: "auto",
                            mt: { xs: 6, md: 10 },
                            height: "1px",
                            background:
                                "linear-gradient(90deg, rgba(245,245,245,0) 0%, rgba(245,245,245,0.16) 50%, rgba(245,245,245,0) 100%)",
                        }}
                    />

                    <Box
                        sx={{
                            width: "100%",
                            maxWidth: 500,
                            mx: "auto",
                            mt: { xs: 5, md: 7 },
                            textAlign: "left",
                        }}
                    >
                        {verificationMode ? (
                            renderVerificationContent()
                        ) : unsubscribeMode ? (
                            renderUnsubscribeContent()
                        ) : (
                            <Box
                                id="signup"
                                component="form"
                                onSubmit={handleSubmit}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    gap: "1rem",
                                    width: "100%",
                                    scrollMarginTop: "24px",
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#f5f5f5",
                                        opacity: 0.8,
                                        fontSize: "0.9rem",
                                    }}
                                >
                                    {t.formDescription}
                                </Typography>

                                <TextField
                                    type="email"
                                    label={t.emailLabel}
                                    value={formData.email}
                                    onChange={(e) =>
                                        setFormData((prev) => ({
                                            ...prev,
                                            email: e.target.value,
                                        }))
                                    }
                                    required
                                    variant="outlined"
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            "& fieldset": {
                                                borderColor: "#f5f5f5",
                                            },
                                        },
                                    }}
                                />

                                <Box sx={{ textAlign: "left" }}>
                                    <Tooltip
                                        title={t.analyticsTooltip}
                                        placement="bottom"
                                        arrow
                                        slotProps={{
                                            tooltip: {
                                                sx: {
                                                    bgcolor: "#1b1b1c",
                                                    color: "#f5f5f5",
                                                    fontSize: "0.875rem",
                                                    p: 1.5,
                                                },
                                            },
                                            arrow: {
                                                sx: {
                                                    color: "#1b1b1c",
                                                },
                                            },
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.analytics}
                                                    onChange={handleCheckboxChange("analytics")}
                                                    sx={{ color: "#f5f5f5" }}
                                                />
                                            }
                                            label={t.analytics}
                                        />
                                    </Tooltip>

                                    <Tooltip
                                        title={t.investmentTooltip}
                                        placement="bottom"
                                        arrow
                                        slotProps={{
                                            tooltip: {
                                                sx: {
                                                    bgcolor: "#1b1b1c",
                                                    color: "#f5f5f5",
                                                    fontSize: "0.875rem",
                                                    p: 1.5,
                                                },
                                            },
                                            arrow: {
                                                sx: {
                                                    color: "#1b1b1c",
                                                },
                                            },
                                        }}
                                    >
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={formData.investment}
                                                    onChange={handleCheckboxChange("investment")}
                                                    sx={{ color: "#f5f5f5" }}
                                                />
                                            }
                                            label={t.investment}
                                        />
                                    </Tooltip>
                                </Box>

                                <TextField
                                    type="text"
                                    name={honeypotName}
                                    value={honeypotValue}
                                    onChange={(e) => setHoneypotValue(e.target.value)}
                                    tabIndex={-1}
                                    autoComplete="off"
                                    sx={{
                                        display: "none",
                                        position: "absolute",
                                        left: "-9999px",
                                    }}
                                />

                                <Button
                                    type="submit"
                                    variant="contained"
                                    disabled={
                                        (!formData.analytics && !formData.investment) ||
                                        !isValidEmail(formData.email)
                                    }
                                    sx={{
                                        backgroundColor: "#f5f5f5",
                                        color: "#202124",
                                        "&:hover": {
                                            backgroundColor: "#e0e0e0",
                                        },
                                    }}
                                >
                                    {t.submitButton}
                                </Button>
                            </Box>
                        )}
                    </Box>
                </Box>

                <Snackbar
                    open={open}
                    autoHideDuration={4000}
                    onClose={handleClose}
                    anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "center",
                    }}
                >
                    <MuiAlert
                        onClose={handleClose}
                        severity={severity}
                        elevation={6}
                        variant="filled"
                        sx={{
                            width: "100%",
                            backgroundColor:
                                severity === "success"
                                    ? "#f5f5f5"
                                    : undefined,
                            color:
                                severity === "success"
                                    ? "#202124"
                                    : undefined,
                        }}
                    >
                        {message}
                    </MuiAlert>
                </Snackbar>

                <Slide direction="up" in={!atPageBottom && !nudgeOpen} mountOnEnter unmountOnExit>
                    <Box
                        sx={{
                            position: "fixed",
                            bottom: { xs: 12, md: 24 },
                            left: 0,
                            right: 0,
                            zIndex: 1250,
                            display: "flex",
                            justifyContent: "center",
                            pointerEvents: "none",
                        }}
                    >
                        <IconButton
                            onClick={scrollToSignup}
                            aria-label={t.nudgeGoto}
                            size="small"
                            sx={{
                                pointerEvents: "auto",
                                color: "#f5f5f5",
                                backgroundColor: "#1b1b1c",
                                border: "1px solid rgba(245,245,245,0.12)",
                                "&:hover": { backgroundColor: "#26262a" },
                            }}
                        >
                            <KeyboardArrowDownIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </Slide>

                <Slide direction="up" in={nudgeOpen} mountOnEnter unmountOnExit>
                    <Box
                        sx={{
                            position: "fixed",
                            bottom: { xs: 12, md: 24 },
                            left: 0,
                            right: 0,
                            zIndex: 1300,
                            display: "flex",
                            justifyContent: "center",
                            px: 1.5,
                            pointerEvents: "none",
                        }}
                    >
                        <Paper
                            elevation={8}
                            sx={{
                                pointerEvents: "auto",
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                px: 2,
                                py: 1,
                                maxWidth: "100%",
                                backgroundColor: "#1b1b1c",
                                backgroundImage: "none",
                                border: "1px solid rgba(245,245,245,0.12)",
                                borderRadius: "999px",
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: "#f5f5f5", opacity: 0.85, fontSize: "0.85rem" }}
                            >
                                {t.nudgeText}
                            </Typography>

                            <IconButton
                                onClick={scrollToSignup}
                                aria-label={t.nudgeGoto}
                                size="small"
                                sx={{
                                    color: "#f5f5f5",
                                    "&:hover": { backgroundColor: "rgba(245,245,245,0.1)" },
                                }}
                            >
                                <KeyboardArrowDownIcon fontSize="small" />
                            </IconButton>

                            <IconButton
                                onClick={dismissNudge}
                                aria-label={t.nudgeClose}
                                size="small"
                                sx={{
                                    color: "#f5f5f5",
                                    opacity: 0.5,
                                    "&:hover": { opacity: 1, backgroundColor: "rgba(245,245,245,0.1)" },
                                }}
                            >
                                <CloseIcon fontSize="small" />
                            </IconButton>
                        </Paper>
                    </Box>
                </Slide>

                <Box
                    component="footer"
                    sx={{
                        color: '#f5f5f5',
                        opacity: 0.6,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 1.5,
                        alignItems: 'center',
                        width: "100%",
                        maxWidth: 1440,
                        mx: "auto",
                        mt: 8,
                        pt: 2,
                    }}
                >
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8rem',
                            color: 'inherit',
                            cursor: 'pointer',
                            '&:hover': { opacity: 1 },
                        }}
                        onClick={() => setImprintOpen(true)}
                    >
                        {t.imprintTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        |
                    </Typography>
                    <Typography
                        variant="body2"
                        sx={{
                            fontSize: '0.8rem',
                            color: 'inherit',
                            cursor: 'pointer',
                            '&:hover': { opacity: 1 },
                        }}
                        onClick={() => setPrivacyOpen(true)}
                    >
                        {t.privacyLink}
                    </Typography>
                </Box>

                <Dialog open={imprintOpen} onClose={() => setImprintOpen(false)} maxWidth="sm" fullWidth slotProps={{
                    paper: {
                        sx: {
                            backgroundColor: '#202124',
                            backgroundImage: 'none',
                        }
                    }
                }}>
                    <DialogTitle>{t.imprintTitle}</DialogTitle>
                    <DialogContent>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                            {t.imprintContent} {email}
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setImprintOpen(false)}>{t.close}</Button>
                    </DialogActions>
                </Dialog>

                <Dialog
                    open={privacyOpen}
                    onClose={() => setPrivacyOpen(false)}
                    maxWidth="sm"
                    fullWidth
                    slotProps={{
                        paper: {
                            sx: {
                                backgroundColor: "#202124",
                                backgroundImage: "none",
                            },
                        },
                    }}
                >
                    <DialogTitle>{t.privacyTitle}</DialogTitle>
                    <DialogContent>
                        <PrivacyContent language={language} />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setPrivacyOpen(false)}>{t.close}</Button>
                    </DialogActions>
                </Dialog>
            </Box>
        </ThemeProvider >
    );
}

export default App;